import * as Sentry from "@sentry/nextjs"
import { Button, Label, Modal, Spinner } from "flowbite-react"
import { useRouter } from "next/router"
import React, { useEffect, useRef, useState } from "react"
import useSWR from "swr"

import { CloseIcon, HomeIcon, LeftSmallIcon } from "@assets/icons"
import CustomImage from "@components/elements/CustomImage"
import Steps from "@components/elements/FormStepper/stepper"
import StripHTMLAndAddEllipses from "@components/elements/StripHTMLAndAddEllipses"
import useAuth from "@hooks/useAuth"
import useToast from "@hooks/useToast"
import BookingSwLogo from "@icons/BookingSwLogo"
import { checkDisplayRules } from "@modules/tenants/OrdersPage/Components/AddOrderModal/ParticipationDetailsForm/ParticipationDetailsForm"
import type { PerOrderFieldsFormAttributes } from "@modules/tenants/OrdersPage/OrderSummaryPage/PerOrderFieldsModal"
import { ASK_PER_BOOKING, ASK_PER_PARTICIPANT } from "@mono/essentials/constants/options"
import { domainPlaceholder, widgetsDomainName } from "@mono/essentials/constants/options"
import * as OrderServices from "@mono/essentials/services/OrderServices"
import * as ProductServices from "@mono/essentials/services/ProductServices"
import type {
  CreateCartRequestType,
  ParticipantsDetailsType,
  UpdatePerBookingFieldsOfCartRequestType,
} from "@mono/essentials/types/CartTypes"
import type { ProductFieldType } from "@mono/essentials/types/OrderTypes"
import { scrollToError } from "@mono/essentials/utils/Common"
import { steps } from "@mono/essentials/utils/Constant"

import AdditionalProductsList from "../AdditionalProducts/AdditionalProductsList"
import CartTotal from "../CartTotal"
import CustomerDetailsForm from "../CustomerDetailsForm"
import CustomerLogin from "../CustomerLogin"
import ParticipationDetailsForm, {
  type ParticipationDetailsFormAttributes,
} from "../ParticipantDetailsForm"
import ProductOverView from "../ProductOverview"
import RentalProductDetailsPreview from "../RentalProductDetailsPreview"

import type { FormikProps } from "formik"

const ProductRentalBuyModal = ({
  show,
  selectedProduct,
  selectedProductData,
  pagePreview,
  closeModal,
  onSuccess,
  handleHomeButtonClick,
  isIntegratedPage,
  setCurrentStepTobacktStep,
  currentStepToBackStep,
  domainDetails,
  productInitialValues = {},
  isSelectedDate,
  cartId,
  updateCart,
  cartItemDetails,
  cartParticipantsDetails,
  isFromCheckOutModal,
  customer_id,
  isCustomWidget = false,
  refetchParticipantsDetails,
  orderSummary,
  isFromPagePreview = false,
  hideStepper = false,
  lastStepCount,
  onSubmit,
  hideQuantityEdit = false,
  productDetails,
}: any) => {
  const [currentStep, setCurrentStep] = useState(
    steps.findIndex((item: any) => item.value === "scheduling")
  )
  const [productInfo, setProductInfo] = useState<any | undefined>(productInitialValues ?? undefined)
  const noOfActiveProductInCategory = selectedProductData?.categories[0]?.active_products_counts
  const [addonProductInfo, setAddonProductInfo] = useState(undefined)
  const [orderSummery, setOrderSummery] = useState<any>(orderSummary ?? undefined)
  const [participantsDetails, setParticipantsDetails] =
    useState<ParticipationDetailsFormAttributes>()
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [customerId, setCustomerId] = useState<string>()
  const router = useRouter()
  const { showToast } = useToast()
  const { loggedInCustomerDetails } = useAuth()
  const { affiliateID } = router.query as any
  const [skipParticipant, setSkipParticipant] = useState(false)
  const participantDetailsRef = useRef<FormikProps<ParticipationDetailsFormAttributes>>(null)
  const customerDetailsRef = useRef<FormikProps<PerOrderFieldsFormAttributes>>(null)
  const perOrderDetailsRef = useRef<FormikProps<PerOrderFieldsFormAttributes>>(null)
  const [perOrderDetailsFieldData, setPerOrderDetailsFieldData] = useState({})
  const [perOrderAllFields, setPerOrderFields] = useState({})
  const [uniqueStep, setUniqueStep] = useState(0)

  const additionalProductsRef = useRef(null)

  const modalContentRef = useRef(null)

  useEffect(() => {
    if (orderSummary) {
      setOrderSummery(orderSummary)
    }
  }, [orderSummary])

  const { data: productFieldsData, isValidating: isProductFieldsDataLoading } = useSWR(
    selectedProduct ? "productFieldsData" : null,
    async () => {
      const selectProductId = selectedProduct || ""
      const { data: getProductFieldsResponse } = await OrderServices.getProductFields(
        selectProductId,
        isIntegratedPage
      )
      if (getProductFieldsResponse) {
        return getProductFieldsResponse.data
      }
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      refreshWhenOffline: false,
      refreshWhenHidden: false,
      refreshInterval: 0,
      shouldRetryOnError: false,
    }
  )

  const {
    data: addOnList = [],
    isValidating: isAddonListLoading,
    mutate: fetchAPIData,
  } = useSWR(
    selectedProduct ? `get_attached_addon_products-${selectedProduct}` : null,
    async () => {
      const { data } = await ProductServices.getProductAddons(selectedProduct, isIntegratedPage, {
        includeRules: true,
        paginate: "0",
      })
      return data || []
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      refreshWhenOffline: false,
      refreshWhenHidden: false,
      refreshInterval: 0,
      shouldRetryOnError: false,
    }
  )

  const goToNextStep = (productInfoAsProp?: any) => {
    setCurrentStep((prev) => {
      const ticketInfo = (Object.keys(productInfo ?? {})?.length ? productInfo : productInfoAsProp)
        ?.units
      if (prev === 0) {
        // Check for valid per_participant or per_booking fields
        if (
          productFieldsData?.filter(
            (field) =>
              (field.ask?.includes(ASK_PER_PARTICIPANT) || field.ask?.includes(ASK_PER_BOOKING)) &&
              checkDisplayRules({
                field,
                ticketInfo,
              }) === "show"
          )?.length
        ) {
          return prev + 1
        } else if (
          // Check for valid add-ons
          addOnList.filter(
            (addon) =>
              checkDisplayRules({
                field: addon as unknown as ProductFieldType,
                formValues: (perOrderDetailsFieldData as any)?.perOrderFieldsDetails?.[0] ?? {},
                ticketInfo,
              }) === "show"
          )?.length
        ) {
          return prev + 2
        }
        return prev + 3
      } else if (prev === 1) {
        // Check for valid add-ons
        if (
          addOnList.filter(
            (addon) =>
              checkDisplayRules({
                field: addon as unknown as ProductFieldType,
                formValues: (perOrderDetailsFieldData as any)?.perOrderFieldsDetails?.[0] ?? {},
                ticketInfo,
              }) === "show"
          )?.length
        ) {
          return prev + 1
        }
        return prev + 2
      }
      return prev + 1
    })
  }

  const perOrderFields = productFieldsData?.filter(
    (item) => item?.ask?.includes("ask_per_booking") && item.allowed_online_booking
  )

  const goToBackStep = () => {
    if (skipParticipant) {
      if (currentStep === 3) {
        if (
          addOnList.filter(
            (addon) =>
              checkDisplayRules({
                field: addon as unknown as ProductFieldType,
                formValues: (perOrderDetailsFieldData as any)?.perOrderFieldsDetails?.[0] ?? {},
                ticketInfo: productInfo?.units,
              }) === "show"
          )?.length
        ) {
          setCurrentStep(2)
        } else if (!(perOrderFields?.length > 0) && addOnList?.length === 0) {
          setCurrentStep(0)
        } else {
          setCurrentStep((prev) => prev - 1)
        }
      } else {
        setCurrentStep(0)
        setSkipParticipant(false)
      }
    } else if (currentStep === 3) {
      if (
        addOnList.filter(
          (addon) =>
            checkDisplayRules({
              field: addon as unknown as ProductFieldType,
              formValues: (perOrderDetailsFieldData as any)?.perOrderFieldsDetails?.[0] ?? {},
              ticketInfo: productInfo?.units,
            }) === "show"
        )?.length
      ) {
        setCurrentStep(2)
      } else if (
        productFieldsData?.filter(
          (field) =>
            (field.ask?.includes(ASK_PER_PARTICIPANT) || field.ask?.includes(ASK_PER_BOOKING)) &&
            checkDisplayRules({
              field,
              ticketInfo: productInfo?.units,
            }) === "show"
        )?.length
      ) {
        setCurrentStep(1)
        setOrderSummery((prev) => {
          return {
            ...prev,
            addonProductData: [],
          }
        })
      } else {
        setCurrentStep(0)
      }
    } else if (currentStep === 2) {
      if (
        productFieldsData?.filter(
          (field) =>
            (field.ask?.includes(ASK_PER_PARTICIPANT) || field.ask?.includes(ASK_PER_BOOKING)) &&
            checkDisplayRules({
              field,
              ticketInfo: productInfo?.units,
            }) === "show"
        )?.length
      ) {
        setCurrentStep(1)
        setOrderSummery((prev) => {
          return {
            ...prev,
            addonProductData: [],
          }
        })
      } else {
        setCurrentStep(0)
      }
    } else {
      setCurrentStep((prev) => (prev >= 1 ? prev - 1 : 0))
    }
    setUniqueStep((prev) => prev + 1)
  }

  const resetCheckOutForm = () => {
    participantDetailsRef?.current?.resetForm()
    customerDetailsRef?.current?.resetForm()
  }

  const handleResetData = (isCancel = false) => {
    setProductInfo(undefined)
    setOrderSummery(undefined)
    setSkipParticipant(false)
    setCurrentStep(0)
    closeModal(isCancel)
    handleHomeButtonClick(isCancel)
    setParticipantsDetails(undefined)
  }

  const onModalClose = () => {
    setProductInfo(undefined)
    setOrderSummery(undefined)
    setSkipParticipant(false)
    setCurrentStep(0)
    closeModal(true)
  }

  useEffect(() => {
    if (Object.keys(productInitialValues).length) {
      setCurrentStep((prev) => (prev >= lastStepCount ? prev : 1))
    }
  }, [productInitialValues])

  const handleCreateCart = async (
    addonProductInfo?: any,
    selectedCustomer?: string,
    perOrderFields?: PerOrderFieldsFormAttributes,
    customerMembershipId?: string
  ) => {
    try {
      const cartItemIdsBeforeUpdateCart = cartItemDetails?.items.map((ci) => ci?.cart_item_id) ?? []
      const selectedCustomerId =
        loggedInCustomerDetails?.id || selectedCustomer || customerId || customer_id
      const _membershipId = customerMembershipId
      const selectedProductPerOrderFields: any = perOrderFields || perOrderAllFields
      if (selectedCustomerId && productInfo) {
        setIsSubmitting(true)
        const createCartPayload: CreateCartRequestType = {
          affiliate_id: affiliateID,
          customer_id: selectedCustomerId,
          items: [
            {
              product_id: selectedProduct,
              quantities: productInfo?.is_Single_day
                ? productInfo?.units?.map((unit) => ({
                    unit_id: unit.unitId ?? "",
                    quantity: unit.unitCount ?? 1,
                    start_at: unit.start_at,
                    end_at: unit.end_at,
                  }))
                : productInfo?.units?.map((unit) => ({
                    unit_id: unit.unitId ?? "",
                    quantity: unit.unitCount ?? 1,
                    start_at: unit.start_at,
                    end_at: unit.end_at,
                    availability_id: unit?.time,
                  })),
              is_updated: updateCart && cartItemDetails.order_id ? 0 : undefined,
              add_on:
                addonProductInfo?.addonDetails?.map((item: any) => ({
                  product_id: item?.addonId,
                  quantities: item?.units?.map((unit) => ({
                    unit_id: unit.unitId ?? "",
                    quantity: unit.unitCount ?? 1,
                  })),
                })) || [],
              slot_id: productInfo.time ?? "",
            },
          ],
        }
        // const perOrderParticipantDetails: any = merge(perOrderDetailsFieldData, selectedProductPerOrderFields, (a, b) =>
        //   isArray(a) ? a.concat(b) : undefined
        // )
        const perOrderParticipantDetails: any = perOrderDetailsFieldData
        if (cartId && cartItemDetails && updateCart)
          createCartPayload.items.unshift(
            ...(cartItemDetails.items || []).map((item) => ({
              ...item,
              product_id: item.product_id,
              slot_id: item.slot_id,
              quantities: item.quantities,
            }))
          )
        const { data: createCartResponse } =
          updateCart && cartId
            ? await OrderServices.updateCart(cartId, createCartPayload, isIntegratedPage)
            : await OrderServices.createCart(createCartPayload, isIntegratedPage)

        if (createCartResponse.cart.id && _membershipId) {
          const payload = {
            code_type: "membership_code",
            code: _membershipId,
          }
          await OrderServices.applyMembershipToCart(
            createCartResponse.cart.id,
            payload,
            isIntegratedPage
          )
        }
        if (participantsDetails?.participantsDetails && createCartResponse.cart.id) {
          const hasValidFields = participantsDetails?.participantsDetails.filter(
            (field) => !(Object.keys(field).length < 3)
          ).length
          if (hasValidFields) {
            const addParticipantsToCartPayload: Record<string, ParticipantsDetailsType[]> = {}
            const selectedCartItem = createCartResponse.cart.items.find((cartItem) => {
              if (updateCart) {
                return !cartItemIdsBeforeUpdateCart.includes(cartItem.id)
              }
              return cartItem.product.id === selectedProduct.id
            })
            if (selectedCartItem) {
              addParticipantsToCartPayload[selectedCartItem.id] =
                participantsDetails.participantsDetails.map((participantDetails) => ({
                  unit_id: (participantDetails.participantUnitId ?? "") as unknown as string,
                  fields: Object.keys(participantDetails)
                    .filter(
                      (fieldName) =>
                        !(
                          fieldName.includes("_id") ||
                          fieldName === "participantUnitId" ||
                          fieldName === "participantGroupName" ||
                          fieldName === "participantIndex"
                        )
                    )
                    .map((fieldName) => ({
                      value: participantDetails[fieldName],
                      product_field_id: participantDetails[`${fieldName}_id`] as unknown as string,
                    })),
                }))
              OrderServices.addParticipantsToCart(
                createCartResponse.cart.id,
                {
                  participants: {
                    ...(updateCart && cartParticipantsDetails?.participants
                      ? cartParticipantsDetails.participants
                      : {}),
                    ...addParticipantsToCartPayload,
                  },
                },
                isIntegratedPage
              )
            }
          }
        }
        if (
          createCartResponse.cart.id &&
          perOrderParticipantDetails &&
          perOrderParticipantDetails.perOrderFieldsDetails?.length &&
          Object.keys(perOrderParticipantDetails.perOrderFieldsDetails[0]).length
        ) {
          const selectedCartItem = createCartResponse.cart.items.find((cartItem) => {
            if (updateCart) {
              return !cartItemIdsBeforeUpdateCart.includes(cartItem.id)
            }
            return cartItem.product.id === selectedProduct
          })

          if (selectedCartItem) {
            const perBookingFieldsPayload: UpdatePerBookingFieldsOfCartRequestType["body"] = []
            perOrderParticipantDetails?.perOrderFieldsDetails.forEach((perBookingFieldDetails) => {
              Object.keys(perBookingFieldDetails)
                .filter((fieldName) => !fieldName.includes("_id"))
                .forEach((fieldName) => {
                  perBookingFieldsPayload.push({
                    value: perBookingFieldDetails[fieldName],
                    product_field_id: perBookingFieldDetails[
                      `${fieldName}_id`
                    ] as unknown as string,
                    cart_item_id: selectedCartItem.id,
                  })
                })
            })
            if (perBookingFieldsPayload.length)
              await OrderServices.updatePerBookingFieldsOfCart(
                {
                  cartId: createCartResponse.cart.id,
                  body: perBookingFieldsPayload,
                },
                isIntegratedPage
              )
          }
        }
        if (
          createCartResponse.cart.id &&
          selectedProductPerOrderFields &&
          selectedProductPerOrderFields.perOrderFieldsDetails?.length &&
          Object.keys(selectedProductPerOrderFields.perOrderFieldsDetails[0]).length
        ) {
          const payload: {
            value: string | number | string[] | boolean
            product_field_id: string
          }[] = []

          selectedProductPerOrderFields?.perOrderFieldsDetails.forEach(
            (perOrderFieldDetails: any) => {
              Object.keys(perOrderFieldDetails)
                .filter((fieldName) => !fieldName.includes("_id"))
                .forEach((fieldName) => {
                  payload.push({
                    value: perOrderFieldDetails[fieldName],
                    product_field_id: perOrderFieldDetails[`${fieldName}_id`] as unknown as string,
                  })
                })
            }
          )
          if (payload.length) {
            await OrderServices.updatePerOrderFieldsOfCart(
              createCartResponse.cart.id,
              payload,
              isIntegratedPage
            )
          }
        }
        onSuccess?.(createCartResponse.cart.id)
        setIsSubmitting(false)
        resetCheckOutForm()
        handleResetData()
      }
    } catch (e: any) {
      Sentry.captureException(e)

      setIsSubmitting(false)
      showToast({
        heading: e?.response?.data?.message ?? "Something went wrong! please try again later",
        type: "danger",
      })
    }
  }

  const handleNextStep = async (
    category_id = "",
    product_id = "",
    productInfo,
    isAddons = false,
    addOnsData,
    participantsDetails,
    perOrderDetailsFieldData
  ) => {
    try {
      setIsLoading(true)
      if (isFromCheckOutModal) {
        const selectProductId = selectedProduct || product_id || ""
        const cartItemIdsBeforeUpdateCart =
          cartItemDetails?.items.map((ci) => ci?.cart_item_id) ?? []
        const { data: getProductFieldsResponse } = await OrderServices.getProductFields(
          selectProductId,
          isIntegratedPage
        )
        if (
          getProductFieldsResponse &&
          getProductFieldsResponse.data &&
          (!getProductFieldsResponse.data.filter(
            (field) =>
              (field.ask?.includes("ask_per_participant") && field.allowed_online_booking) ||
              (field.ask?.includes("ask_per_booking") && field.allowed_online_booking)
          ).length ||
            isAddons)
        ) {
          if (addOnList?.length > 0 && !isAddons) {
            setCurrentStep(2)
          } else {
            if (customer_id && productInfo) {
              setIsSubmitting(true)
              const createCartPayload: CreateCartRequestType = {
                affiliate_id: affiliateID,
                customer_id,
                items: [
                  {
                    product_id: selectedProduct,
                    quantities: productInfo?.is_Single_day
                      ? productInfo?.units?.map((unit) => ({
                          unit_id: unit.unitId ?? "",
                          quantity: unit.unitCount ?? 1,
                          start_at: unit.start_at,
                          end_at: unit.end_at,
                        }))
                      : productInfo?.units?.map((unit) => ({
                          unit_id: unit.unitId ?? "",
                          quantity: unit.unitCount ?? 1,
                          start_at: unit.start_at,
                          end_at: unit.end_at,
                          availability_id: unit?.time,
                        })),
                    is_updated: updateCart && cartItemDetails?.order_id ? 0 : undefined,
                    add_on:
                      addOnsData?.addonDetails?.map((item: any) => ({
                        product_id: item?.addonId,
                        quantities: item?.units?.map((unit) => ({
                          unit_id: unit.unitId ?? "",
                          quantity: unit.unitCount ?? 1,
                        })),
                      })) || [],
                    slot_id: productInfo.time ?? "",
                  },
                ],
              }
              if (cartId && cartItemDetails && updateCart)
                createCartPayload.items.unshift(
                  ...(cartItemDetails.items || []).map((item) => ({
                    ...item,
                    product_id: item.product_id,
                    slot_id: item.slot_id,
                    quantities: item.quantities,
                  }))
                )
              const { data: createCartResponse } =
                updateCart && cartId
                  ? await OrderServices.updateCart(cartId, createCartPayload, isIntegratedPage)
                  : await OrderServices.createCart(createCartPayload, isIntegratedPage)

              // Participant Data

              if (participantsDetails?.participantsDetails && createCartResponse.cart.id) {
                const hasValidFields = participantsDetails?.participantsDetails.filter(
                  (field) => !(Object.keys(field).length < 3)
                ).length
                if (hasValidFields) {
                  const addParticipantsToCartPayload: Record<string, ParticipantsDetailsType[]> = {}
                  const selectedCartItem = createCartResponse.cart.items.find((cartItem) => {
                    if (updateCart) {
                      return !cartItemIdsBeforeUpdateCart.includes(cartItem.id)
                    }
                    return cartItem.product.id === selectedProduct.id
                  })
                  if (selectedCartItem) {
                    addParticipantsToCartPayload[selectedCartItem?.id] =
                      participantsDetails.participantsDetails.map((participantDetails) => ({
                        unit_id: (participantDetails.participantUnitId ?? "") as unknown as string,
                        fields: Object.keys(participantDetails)
                          .filter(
                            (fieldName) =>
                              !(
                                fieldName.includes("_id") ||
                                fieldName === "participantUnitId" ||
                                fieldName === "participantGroupName" ||
                                fieldName === "participantIndex"
                              )
                          )
                          .map((fieldName) => ({
                            value: participantDetails[fieldName],
                            product_field_id: participantDetails[
                              `${fieldName}_id`
                            ] as unknown as string,
                          })),
                      }))
                    await OrderServices.addParticipantsToCart(
                      createCartResponse.cart.id,
                      {
                        participants: {
                          ...(updateCart && cartParticipantsDetails?.participants
                            ? cartParticipantsDetails.participants
                            : {}),
                          ...addParticipantsToCartPayload,
                        },
                      },
                      isIntegratedPage
                    )
                  }
                  await refetchParticipantsDetails()
                }
              }

              if (
                createCartResponse.cart.id &&
                perOrderDetailsFieldData &&
                perOrderDetailsFieldData.perOrderFieldsDetails?.length &&
                Object.keys(perOrderDetailsFieldData.perOrderFieldsDetails[0]).length
              ) {
                const selectedCartItem = createCartResponse.cart.items.find((cartItem) => {
                  if (updateCart) {
                    return !cartItemIdsBeforeUpdateCart.includes(cartItem.id)
                  }
                  return cartItem.product.id === selectedProduct
                })

                if (selectedCartItem) {
                  const perBookingFieldsPayload: UpdatePerBookingFieldsOfCartRequestType["body"] =
                    []
                  perOrderDetailsFieldData?.perOrderFieldsDetails.forEach(
                    (perBookingFieldDetails) => {
                      Object.keys(perBookingFieldDetails)
                        .filter((fieldName) => !fieldName.includes("_id"))
                        .forEach((fieldName) => {
                          perBookingFieldsPayload.push({
                            value: perBookingFieldDetails[fieldName],
                            product_field_id: perBookingFieldDetails[
                              `${fieldName}_id`
                            ] as unknown as string,
                            cart_item_id: selectedCartItem.id,
                          })
                        })
                    }
                  )
                  if (perBookingFieldsPayload.length)
                    await OrderServices.updatePerBookingFieldsOfCart(
                      {
                        cartId: createCartResponse.cart.id,
                        body: perBookingFieldsPayload,
                      },
                      isIntegratedPage
                    )
                }
              }

              onSuccess?.(createCartResponse.cart.id)
              setIsSubmitting(false)
              resetCheckOutForm()
              handleResetData()
            }
          }
        } else {
          goToNextStep(productInfo)
        }
      } else {
        await onSubmit?.(productInfo), goToNextStep(productInfo)
      }
    } catch (e) {
      Sentry.captureException(e)
      setIsSubmitting(false)
      console.error(e?.response?.data?.message)
      showToast({ heading: e?.response?.data?.message, type: "danger" })
    } finally {
      setIsLoading(false)
    }
  }

  const getModalContent = (steps: number) => {
    switch (steps) {
      case 0:
        return (
          <RentalProductDetailsPreview
            pagePreview={pagePreview}
            selectedProduct={selectedProduct}
            selectedProductData={selectedProductData}
            setProductInfo={setProductInfo}
            goToNextStep={handleNextStep}
            setOrderSummery={setOrderSummery}
            isIntegratedPage={isIntegratedPage}
            domainDetails={domainDetails}
            isSelectedDate={isSelectedDate}
            isLoading={isLoading}
            formInitialValues={{
              available_option: productInfo?.available_option ?? "",
              duration_type: productInfo?.duration_type ?? "",
              end_date: productInfo?.end_date ?? "",
              end_time: productInfo?.end_time ?? "",
              multi_day_fix_time: productInfo?.multi_day_fix_time ?? "",
              quantity: productInfo?.quantity ?? 1,
              start_date: productInfo?.start_date,
              start_time: productInfo?.start_time,
              units: productInfo?.units ?? [],
              slot_id: productInfo?.slot_id ?? "",
              product_availability_id: productInfo?.product_availability_id ?? "",
            }}
            isModal={`${currentStepToBackStep}- ${currentStep}-${uniqueStep}`}
            cartId={cartId}
            hideQuantityEdit={hideQuantityEdit}
            isFromProductRentalBuyModal
          />
        )
      case 1:
        return isAddonListLoading || isProductFieldsDataLoading ? (
          <div className={"text-center"}>
            <Spinner size={"md"} className={"text-primary-400 fill-white"} />
          </div>
        ) : productInfo ? (
          <div>
            <div className={"md:grid lg:grid-cols-9 md:grid-cols-9 sm:grid-cols-12"}>
              <div className={"lg:col-span-6 md:col-span-6 sm:col-span-6 md:mb-0 mb-5"}>
                <div className={"col-span-3 md:border-r md:pr-6"}>
                  <ParticipationDetailsForm
                    productInfo={productInfo}
                    selectedProductId={selectedProduct}
                    handleClose={goToBackStep}
                    hideBackButton
                    // Removing this because it was causing the issue of participantsDetails is set in dynamic rental product as we  don't have participantsDetails in dynamic rental product
                    // onSuccess={(values) => {
                    //   setParticipantsDetails((prev) => ({
                    //     participantsDetails: [],
                    //   })
                    // }}
                    skipPage={() => {
                      if (addOnList?.length > 0) {
                        setCurrentStep(2)
                      } else {
                        setCurrentStep(3)
                      }
                      setSkipParticipant(true)
                    }}
                    onPerOrderSucess={(perOrderDetails) => {
                      setPerOrderDetailsFieldData(perOrderDetails)
                    }}
                    participantDetailsRef={participantDetailsRef}
                    perOrderDetailsRef={perOrderDetailsRef}
                    formInitialValues={participantsDetails}
                    hideDefaultContainer
                    isIntegratedPage={isIntegratedPage}
                    isWidgetpage
                    productFieldsData={productFieldsData}
                    perOrderInitialValues={perOrderDetailsFieldData}
                  />
                </div>
              </div>
              <div
                className={
                  "lg:col-span-3 md:col-span-3 sm:col-span-6 md:pl-4 top-0 md:sticky self-start"
                }
              >
                <ProductOverView
                  selectedDurationDetails={orderSummery?.selectedProductDurationDetails}
                  selectedProductDetail={selectedProductData}
                  selectedPriceTypeData={orderSummery?.selectedPriceTypeData}
                  domainDetails={domainDetails}
                  handleNextStep={async () => {
                    if (
                      (productFieldsData ?? [])?.filter(
                        (field) =>
                          (field.ask.includes(ASK_PER_PARTICIPANT) ||
                            field.ask.includes(ASK_PER_BOOKING)) &&
                          checkDisplayRules({
                            field,
                            ticketInfo: productInfo?.units,
                          }) === "show"
                      )?.length === 0
                    ) {
                      goToNextStep()
                    }
                    if (
                      participantDetailsRef.current?.isValid ||
                      perOrderDetailsRef?.current?.isValid
                    ) {
                      const data: any = await Promise.all([
                        participantDetailsRef.current?.submitForm(),
                        perOrderDetailsRef?.current?.submitForm(),
                      ])

                      const hasConditionalAddons = addOnList.filter(
                        (addon) =>
                          checkDisplayRules({
                            field: addon as unknown as ProductFieldType,
                            formValues: (data[1] as any)?.perOrderFieldsDetails?.[0] ?? {},
                            ticketInfo: productInfo?.units,
                          }) === "show"
                      )?.length

                      let isValidForm = false

                      const perOrderFields = productFieldsData?.filter(
                        (item) =>
                          item?.ask?.includes("ask_per_booking") && item.allowed_online_booking
                      )
                      if (perOrderFields?.length) {
                        if (data[1]?.perOrderFieldsDetails?.length) {
                          isValidForm = true
                        }
                      }

                      if (isValidForm) {
                        if (addOnList?.length > 0) {
                          if (hasConditionalAddons) {
                            setCurrentStep(2)
                          } else {
                            if (isFromCheckOutModal) {
                              handleNextStep(
                                "",
                                selectedProduct,
                                productInfo,
                                true,
                                undefined,
                                data[0],
                                data[1]
                              )
                            } else {
                              setCurrentStep(3)
                            }
                          }
                        } else {
                          if (isFromCheckOutModal) {
                            handleNextStep(
                              "",
                              selectedProduct,
                              productInfo,
                              true,
                              undefined,
                              data[0],
                              data[1]
                            )
                          } else {
                            setCurrentStep(3)
                          }
                        }
                      } else {
                        scrollToError()
                      }
                    } else {
                      scrollToError()
                    }
                  }}
                  isLoading={isLoading}
                  isSubmitting={isSubmitting}
                />
              </div>
            </div>
          </div>
        ) : null
      case 2:
        return (
          <div className={"md:grid lg:grid-cols-9 md:grid-cols-9 sm:grid-cols-12"}>
            <div className={"lg:col-span-6 md:col-span-6 sm:col-span-6 md:mb-0 mb-5"}>
              <div className={"col-span-3 md:border-r md:pr-6"}>
                <AdditionalProductsList
                  formikRef={additionalProductsRef}
                  formInitialValues={addonProductInfo}
                  addOnList={addOnList.filter(
                    (addon) =>
                      checkDisplayRules({
                        field: addon as unknown as ProductFieldType,
                        formValues:
                          (perOrderDetailsFieldData as any)?.perOrderFieldsDetails?.[0] ?? {},
                        ticketInfo: productInfo?.units,
                      }) === "show"
                  )}
                  isIntegratedPage={isIntegratedPage}
                  handleNextStep={(addonProductInfo) => {
                    setAddonProductInfo(addonProductInfo)
                    if (isFromCheckOutModal) {
                      handleNextStep(
                        "",
                        selectedProduct,
                        productInfo,
                        true,
                        addonProductInfo,
                        participantsDetails,
                        perOrderDetailsFieldData
                      )
                    } else {
                      setCurrentStep(3)
                    }
                  }}
                  participantsDetails={participantsDetails}
                  domainDetails={domainDetails}
                  isLoader={isLoading || isAddonListLoading}
                  setOrderSummery={setOrderSummery}
                  productInfo={productInfo}
                />
              </div>
            </div>
            <div
              className={
                "lg:col-span-3 md:col-span-3 sm:col-span-6 md:pl-4 top-0 md:sticky self-start"
              }
            >
              <ProductOverView
                selectedProductDetail={selectedProductData}
                selectedDurationDetails={orderSummery?.selectedProductDurationDetails}
                selectedAddOnData={orderSummery?.addonProductData}
                selectedPriceTypeData={orderSummery?.selectedPriceTypeData}
                domainDetails={domainDetails}
                handleNextStep={() => {
                  additionalProductsRef?.current?.submitForm()
                  scrollToError()
                }}
                isLoading={isLoading}
                isSubmitting={isSubmitting}
              />
            </div>
          </div>
        )
      case 3:
        return (
          <div className={"md:grid lg:grid-cols-9 md:grid-cols-9 sm:grid-cols-12"}>
            <div className={"lg:col-span-6 md:col-span-6 sm:col-span-6 md:mb-0 mb-5"}>
              <div className={"col-span-3 md:border-r md:pr-6"}>
                <CustomerDetailsForm
                  onSuccess={(customer_id, perOrderParticipantDetails, customerMembershipId) => {
                    handleCreateCart(
                      addonProductInfo,
                      customer_id,
                      perOrderParticipantDetails,
                      customerMembershipId
                    )
                  }}
                  customerDetailsRef={customerDetailsRef}
                  isIntegratedPage={isIntegratedPage}
                  selectedProductId={selectedProduct}
                  domainDetails={domainDetails}
                />
              </div>
            </div>
            <div className={"lg:col-span-3 md:col-span-3 sm:col-span-6 md:pl-4"}>
              <ProductOverView
                selectedProductDetail={selectedProductData}
                selectedDurationDetails={orderSummery?.selectedProductDurationDetails}
                selectedAddOnData={orderSummery?.addonProductData}
                selectedPriceTypeData={orderSummery?.selectedPriceTypeData}
                domainDetails={domainDetails}
                handleNextStep={() => {
                  customerDetailsRef.current?.submitForm()
                  scrollToError()
                }}
                isLoading={isSubmitting}
                submitButtonDisabled={isSubmitting}
              />
            </div>
          </div>
        )
      default:
        return null
    }
  }

  const handleCloseButton = () => {
    // Handle non-first step case
    if (currentStep > 0) {
      goToBackStep()
      return
    }

    // Handle first step cases
    const isFirstStepWithSingleProduct = noOfActiveProductInCategory === 1 && currentStep === 0

    if (!setCurrentStepTobacktStep) {
      handleResetData(true)
      return
    }

    if (isCustomWidget) {
      handleResetData(true)
      return
    }

    if (isFirstStepWithSingleProduct) {
      setProductInfo(undefined)
      setOrderSummery(undefined)
      setSkipParticipant(false)
      setCurrentStep(0)
      setParticipantsDetails(undefined)
      if (productDetails?.show_specific_product_detail) {
        handleResetData(true)
      } else {
        setCurrentStepTobacktStep?.(1)
      }
    } else if (currentStep === 0) {
      if (setCurrentStepTobacktStep) {
        if (isCustomWidget || productDetails?.show_specific_product_detail) {
          handleResetData(true)
        } else {
          setCurrentStepTobacktStep?.(currentStepToBackStep - 1)
        }
      } else {
        handleResetData(true)
      }
    } else {
      setCurrentStepTobacktStep((prev) => (prev - 1 > 0 ? prev - 1 : prev))
    }
  }

  useEffect(() => {
    if (window.innerWidth <= 767 && modalContentRef && modalContentRef?.current) {
      modalContentRef?.current.scrollTo({
        top: 0,
        behavior: "smooth",
      })
    }
  }, [currentStep])

  const selectedProductDetail = orderSummery?.selectedProductDetail ?? selectedProductData

  return (
    <Modal
      show={show}
      size={"7xl"}
      className={"widget-theme-wrapper"}
      theme={{
        content: {
          inner:
            "relative rounded-lg bg-transparent shadow dark:bg-gray-700 flex flex-col max-h-[92vh] px-2 lg:px-0",
          base: `w-full !max-w-[1180px]`,
        },
      }}
    >
      <Modal.Body className={"!bg-transparent rounded-lg"}>
        {/* Stepper  */}
        {!hideStepper ? (
          <div className={"flex items-center px-4 mb-2 bg-white rounded-lg sm:px-8"}>
            <div className={"border-r border-neutral-300"} onClick={() => handleResetData(true)}>
              <HomeIcon className={"w-5 h-5 mr-2 font-bold cursor-pointer text-primary-600"} />
            </div>
            <div
              className={
                "flex items-center justify-center border-r cursor-pointer border-neutral-300 sm:pr-6"
              }
              onClick={handleCloseButton}
            >
              <LeftSmallIcon width={18} height={18} className={"mx-2 text-primary-600"} />
              <div className={"hidden text14Medium text-primary-600 sm:block"}>Back</div>
            </div>
            <Steps
              stepWrapperClassName={"w-full "}
              wrapperClassName={"w-full shadow-none"}
              steps={steps}
              currentStep={currentStep}
              // steps={
              //   addOnList?.length === 0 && (skipParticipant || perOrderFields?.length === 0)
              //     ? steps.filter(
              //         (step) =>
              //           step.value !== "additionalProducts" && step.value !== "participantDetails",
              //       )
              //     : skipParticipant || perOrderFields?.length === 0
              //       ? steps.filter((step) => step.value !== "participantDetails")
              //       : addOnList?.length === 0
              //         ? steps.filter((step) => step.value !== "additionalProducts")
              //         : steps
              // }
              // currentStep={
              //   (addOnList?.length > 0 && skipParticipant && currentStep === 3) ||
              //   (skipParticipant && currentStep === 2) ||
              //   (addOnList?.length === 0 && currentStep === 3)
              //     ? currentStep - 1
              //     : currentStep
              // }
              innerClassName={"flex"}
              isWidget
            />
          </div>
        ) : null}

        {/* Content */}
        <div className={"rounded-t-lg"}>
          <div className={"px-6 py-4 bg-white border-b rounded-t-lg border-neutral-200"}>
            <div className={"sm:flex block sm:items-center items-start justify-between gap-2"}>
              <div className={"flex sm:items-center items-start"}>
                {domainDetails?.logo ? (
                  <div className={"mr-2 pr-2 border-r w-fit border-neutral-300"}>
                    {domainDetails?.logo !== "" ? (
                      <CustomImage
                        src={domainDetails?.logo}
                        className={"w-auto h-9"}
                        height={40}
                        width={80}
                        fill={false}
                      />
                    ) : (
                      <BookingSwLogo className={"mr-2"} />
                    )}
                  </div>
                ) : null}
                <div>
                  <div className={"lg:text18Medium text14Medium lg:w-auto text-neutral-900"}>
                    <span className={"line-clamp-1"}>{selectedProductDetail?.name}</span>
                  </div>
                  {selectedProductDetail?.widget_description && (
                    <StripHTMLAndAddEllipses
                      content={selectedProductDetail?.widget_description}
                      maxLength={140}
                      className={"lg:!text14Regular !text12Regular !text-neutral-1000 line-clamp-2"}
                    />
                  )}
                </div>
              </div>
              <div className={"cart-logo-container"}>
                <div
                  className={
                    "flex items-center sm:justify-normal justify-between gap-4 whitespace-nowrap"
                  }
                >
                  <div
                    className={
                      "sm:flex inline-flex sm:flex-nowrap flex-wrap gap-1 mt-1.5 sm:mt-0 sm:gap-0"
                    }
                  >
                    {isIntegratedPage ? (
                      <CustomerLogin isIntegratedPage={isIntegratedPage} />
                    ) : null}
                    <CartTotal orderSummary={orderSummery} domainDetails={domainDetails} />
                  </div>
                  <div onClick={() => onModalClose()}>
                    <CloseIcon className={"h-4.5 w-4.5 cursor-pointer"} />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            className={`bg-whiteBlack-100 scrollbar ${
              currentStep === 0 && pagePreview === "with_all_details"
                ? "md:py-6 sm:py-3 py-2"
                : "md:p-6 sm:p-3 p-2"
            } overflow-auto max-h-[calc(92vh-215px)]`}
            ref={modalContentRef}
          >
            {getModalContent(currentStep)}
          </div>
        </div>
        <div
          className={`flex flex-1 flex-row items-center justify-between border-t border-neutral-200 bg-whiteBlack-100 sm:px-6 sm:py-4 px-4 py-3`}
        >
          <div className={"flex items-center justify-between w-full"}>
            <div className={"flex flex-col"}>
              <div className={"paragraph13 text-neutral-1000"}>
                Powered by{" "}
                <a href={`https://www${domainPlaceholder}`} target={"_blank"} rel={"noreferrer"}>
                  <span className={"text-[#2F69FE] text-Medium"}>{`${widgetsDomainName}`}</span>
                </a>
              </div>
              {domainDetails && domainDetails?.currency_iso3 ? (
                <div
                  className={"text12Medium text-neutral-600"}
                >{`All prices are in ${domainDetails?.currency_iso3}`}</div>
              ) : (
                ""
              )}
            </div>
            <div className={"mr-2 w-fit"}>
              <a href={`https://www${domainPlaceholder}`} target={"_blank"} rel={"noreferrer"}>
                <BookingSwLogo />
              </a>
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  )
}

export default ProductRentalBuyModal
